import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, Check, Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rivaaz_admin_token";

export default function AdminPrint() {
  const token = localStorage.getItem(TOKEN_KEY);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [secret, setSecret] = useState("");

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/print/queue?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQueue(data);
      } catch {
        toast.error("Failed to load print queue");
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [token]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const cloudprntUrl = `${process.env.REACT_APP_BACKEND_URL}/api/print/cloudprnt${
    secret.trim() ? `?s=${encodeURIComponent(secret.trim())}` : ""
  }`;

  const copy = (text, label = "URL") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const STATUS_STYLES = {
    pending: "bg-yellow-100 text-yellow-700",
    sent: "bg-blue-100 text-blue-700",
    printed: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="admin-print-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-bold text-[#57534E] hover:text-[#C9853E] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <div className="rounded-2xl p-3 bg-[#C9853E]/10 text-[#C9853E]">
          <Printer className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0E0E1A]">
            Kitchen Printer
          </h1>
          <p className="text-[#57534E] text-sm">Star TSP143IV-UE (CloudPRNT)</p>
        </div>
      </div>

      <section className="mt-10 bg-white border border-[#E7E5E4] rounded-3xl p-6 md:p-8 space-y-5" data-testid="setup-card">
        <h2 className="font-display text-xl font-bold text-[#0E0E1A]">
          One-time setup
        </h2>
        <ol className="space-y-4 text-sm text-[#0E0E1A]">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0E0E1A] text-white font-bold inline-flex items-center justify-center text-xs">1</span>
            <div>
              <strong>Get the printer's IP address.</strong> Hold the FEED button on the front of the printer while powering it on. A test page prints showing the IP (e.g. <code className="bg-[#FAF8F5] px-1 py-0.5 rounded">192.168.1.45</code>).
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0E0E1A] text-white font-bold inline-flex items-center justify-center text-xs">2</span>
            <div>
              From a device on the <strong>same WiFi</strong> as the printer, open <code className="bg-[#FAF8F5] px-1 py-0.5 rounded">http://&lt;printer-ip&gt;/</code> in a browser. Log in (default username/password is usually <code>root</code> / <code>public</code>).
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0E0E1A] text-white font-bold inline-flex items-center justify-center text-xs">3</span>
            <div>
              Open <strong>Network &rarr; CloudPRNT</strong>. Set:
              <ul className="mt-2 ml-1 space-y-1 list-disc list-inside marker:text-[#C9853E]">
                <li><strong>CloudPRNT</strong>: Enable</li>
                <li><strong>Server URL</strong>: paste the URL below</li>
                <li><strong>Poll Interval</strong>: 5 seconds (recommended)</li>
                <li><strong>Type</strong>: Polling</li>
              </ul>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0E0E1A] text-white font-bold inline-flex items-center justify-center text-xs">4</span>
            <div>Save & restart the printer. That's it — every new website order will print automatically.</div>
          </li>
        </ol>

        <div className="bg-[#FAF8F5] border border-[#E7E5E4] rounded-2xl p-5 mt-4">
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#57534E]">
            Shared secret (if you set CLOUDPRNT_SECRET in backend env)
          </label>
          <input
            data-testid="cloudprnt-secret-input"
            type="text"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="leave empty if not configured"
            className="mt-2 w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-[#C9853E]"
          />
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#57534E] mt-4 block">
            CloudPRNT Server URL → paste into printer
          </label>
          <div className="mt-2 flex gap-2">
            <input
              data-testid="cloudprnt-url-display"
              readOnly
              value={cloudprntUrl}
              className="flex-1 bg-white border border-[#E7E5E4] rounded-xl px-4 py-2.5 text-sm font-mono"
            />
            <button
              data-testid="cloudprnt-url-copy"
              onClick={() => copy(cloudprntUrl)}
              className="inline-flex items-center gap-1.5 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-xl px-4 py-2.5 text-sm font-bold"
            >
              <Copy className="w-4 h-4" /> Copy
            </button>
          </div>
        </div>
      </section>

      <section className="mt-10" data-testid="queue-section">
        <h2 className="font-display text-xl font-bold text-[#0E0E1A] mb-4">
          Recent print jobs
        </h2>
        {loading ? (
          <div className="text-[#57534E]"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>
        ) : queue.length === 0 ? (
          <p className="text-[#57534E] text-sm">No jobs yet. They'll appear here as orders come in.</p>
        ) : (
          <div className="bg-white border border-[#E7E5E4] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F5] text-[#57534E] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">Order</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((j) => (
                  <tr key={j.id} className="border-t border-[#E7E5E4]" data-testid={`print-job-${j.id}`}>
                    <td className="px-4 py-3 text-[#57534E]">
                      {new Date(j.created_at).toLocaleString("en-CA", { timeZone: "America/Edmonton" })}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#0E0E1A]">
                      {j.order_id?.slice(0, 8) || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_STYLES[j.status] || ""}`}>
                        {j.status === "printed" && <Check className="w-2.5 h-2.5 inline mr-1" />}
                        {j.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-[#57534E]">{j.attempts || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-10 text-xs text-[#57534E]">
        <p>Need help? See Star Micronics' <a
          href="https://www.starmicronics.com/support/cloudprnt"
          target="_blank" rel="noopener noreferrer"
          className="text-[#C9853E] font-bold inline-flex items-center gap-1"
        >CloudPRNT documentation <ExternalLink className="w-3 h-3" /></a>.</p>
      </section>
    </div>
  );
}
